<template>
  <v-layout
    class="white"
    align-space-between
    justify-start
    column
    fill-height
  >
    <v-flex
      shrink
      class="sz_background_light"
      py-3
    >
      <v-flex
        md10
        offset-md1
        sm12
        offset-sm0
      >
        <v-layout>
          <v-flex shrink>
            <AppLogo width="60" />
          </v-flex>
          <v-flex ml-5>
            <h1 class="display-4">
              {{ $t('sheet.sheetOne.title') }}
            </h1>
            <h2 class="display-1">
              {{ sectionTitle }}
            </h2>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-flex>
    <v-flex v-if="resourceLoading === true">
      <PageLoader />
    </v-flex>
    <v-flex v-else>
      <v-flex
        v-if="!resourceLoading"
        id="sheet-container"
        md10
        offset-md1
        sm12
        offset-sm0
      >
        <v-layout
          :mt-5="smAndUp"
          class="stepper__wrapper"
          mb-3
          fill-height
        >
          <v-flex
            v-if="$vuetify.breakpoint.smAndUp"
            pa-4
            sm3
            class="stepper__header"
          >
            <sheetTableOfContents :header-comment-mark="headerCommentMark" />
          </v-flex>
          <v-dialog
            v-else
            v-model="contentDialog"
            hide-overlay
            persistent
            scrollable
            fullscreen
          >
            <v-layout
              style="background: #fff; overflow: scroll"
              fill-height
            >
              <sheetTableOfContents
                :header-comment-mark="headerCommentMark"
                @closeDialog="contentDialog = false"
              />
            </v-layout>
          </v-dialog>
          <v-flex
            px-4
            pt-4
            pb-0
            xs12
            sm9
            :class="isCurrentEdition ? 'white' : 'deprecated deprecated_background'"
          >
            <v-layout
              v-if="$vuetify.breakpoint.xsOnly"
              align-center
              justify-space-between
            >
              <v-flex>
                <a
                  class="link_iconed"
                  @click="goToFamilyCard"
                >
                  <v-icon>keyboard_arrow_left</v-icon> {{ $t('sheet.cancel_button') }}</a>
              </v-flex>
              <v-flex shrink>
                <a @click="contentDialog = true">
                  <BaseIcon>
                    <MenuIcon />
                  </BaseIcon>
                </a>
              </v-flex>
            </v-layout>
            <router-view />
          </v-flex>
        </v-layout>
      </v-flex>
    </v-flex>

    <leaveConfirmDialog ref="confirm" />
  </v-layout>
</template>

<script>
import AppLogo from '@/components/widgets/AppLogo';
import BaseIcon from '@/icons/BaseIcon';
import MenuIcon from '@/icons/MenuIcon';
import sheetTableOfContents from '@/components/widgets/sheet/SheetTableOfContents';
import tableOfC from '@/constants/sheet/sheetOne';
import leaveConfirmDialog from '@/components/widgets/LeaveConfirmDialog';
import auditPermissionsAndStatuses from '@/mixins/auditPermissionsAndStatuses';
import PageLoader from '@/components/widgets/PageLoader';

export default {
    name: 'SheetOne',
    components: { PageLoader, AppLogo, sheetTableOfContents, BaseIcon, MenuIcon, leaveConfirmDialog },
    mixins: [auditPermissionsAndStatuses],
    beforeRouteLeave (to, from, next) {
        this.$store.commit('sheet/clearSheetOne');
        this.$store.commit('family/setFamily', null);
        this.$store.commit('sheet/setSheetMeetingQuestions', null);
        next();
    },
    beforeRouteUpdate (to, from, next) {
        this.resourceLoading = true;
        this.getAuditComments(this.sheetType(to.name));
        this.getFamilyAndResource();
        next();
    },
    props: {
        familyId: {
            required: true
        }
    },
    data () {
        return {
            resourceLoading: true,
            contentDialog: false
        };
    },
    computed: {
        smAndUp () {
            return this.$vuetify.breakpoint.smAndUp;
        },
        sectionTitle () {
            let header = '';
            this.$store.getters['sheet/getTableOfContentsList'].forEach((el) => {
                const _i = el.find((item) => {
                    return item && item.name === this.$route.name;
                });
                if (_i) {
                    header = _i.header;
                }
            });
            return header;
        },
        headerCommentMark () {
            return {
                1: this.showSheetCommentMarkBySheet('meeting'),
                2: this.showSheetCommentMarkBySheet('reflection'),
                3: this.showSheetCommentMarkBySheet('decision')
            };
        },
        allAuditComments () {
            return this.$store.getters['audit/getAllComments'];
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        }
    },
    created () {
        this.$store.dispatch('sheet/addTableOfContentsList', tableOfC);
        this.addHiddenSelectors();
        this.getFamilyAndResource();
    },
    methods: {
        addHiddenSelectors () {
            tableOfC.forEach((el) => {
                el.forEach((item) => {
                    if (item && item.variable === true) {
                        this.$store.commit('sheet/addHiddenSelector', item.selector);
                    }
                });
            });
        },
        goToFamilyCard () {
            this.$router.push({ name: 'family-card', params: { id: this.familyId } });
        },
        showSheetCommentMarkBySheet (sheetType) {
            if (this.showSheetCommentMark) {
                if (
                    this.allAuditComments.some((el) => {
                        return el.sheet_type === sheetType;
                    })
                ) {
                    const comments = this.allAuditComments.find((el) => {
                        return el.sheet_type === sheetType;
                    }).data;
                    return comments && comments.length > 0;
                }
            }
            return false;
        },
        getAuditComments (sheetType) {
            this.$store.dispatch('audit/getAllAuditComments', sheetType);
            this.$store.dispatch('audit/getAuditComment', {
                params: {
                    sheetType
                }
            });
        },
        sheetType (routeName) {
            if (routeName === 'sheet-one-meeting') return 'meeting';
            if (routeName === 'sheet-one-reflection') return 'reflection';
            if (routeName === 'sheet-one-decision') return 'decision';
            return '';
        },
        getFamilyAndResource () {
            this.$store
                .dispatch('family/getFamily', { id: this.$route.params.familyId, endpointLabel: 'family' })
                .then(({ data }) => {
                    this.getAuditComments(this.sheetType(this.$route.name));

                    this.$store
                        .dispatch('sheet/loadSheetOneResource', {
                            sheetId: 1,
                            editionName: data.edition.name
                        })
                        .finally(() => {
                            this.resourceLoading = false;
                        });
                });
        }
    }
};
</script>

<style scoped></style>
